import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createTextNode, $getRoot, $getSelection, $isRangeSelection } from 'lexical';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useCallback, useContext, useEffect, useState } from 'react';
import { UploadPublicDocument } from 'services';
import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { AuthContext } from '@urbanx/agx.auth.client';
import { UploadedDocument, useRemovePublicDocument } from '@urbanx/agx.agencies.client';
import {
  AgxButton,
  Icon_Upload,
  AgxModal,
  AgxColumn,
  AgxRow,
  AgxTextInput,
} from '@urbanx/agx.ui-components2';
import { AgxFileUpload } from '@urbanx/agx-ui-components';

interface AddFilePluginProps {
  disabled?: boolean;
  maxLength?: number;
}

const AddFilePlugin = ({ disabled = false, maxLength }: AddFilePluginProps) => {
  const { agencyId } = useParams();
  const { getAuthToken } = useContext(AuthContext);
  const [editor] = useLexicalComposerContext();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<UploadedDocument | null>(null);
  const [uploadedFilePath, setUploadedFilePath] = useState<string>('');
  const [displayText, setDisplayText] = useState('');
  const [characterError, setCharacterError] = useState('');

  const { mutate: uploadPublicDocument, isPending: isUploading } = useMutation({
    mutationFn: UploadPublicDocument,
    onSuccess: (uploadedFile) => {
      if (uploadedFile) {
        setUploadedFilePath(uploadedFile.publicPath ?? '');
        setSelectedFile(uploadedFile);
      }
    },
    onError: (error) => {
      console.error('Upload failed:', error);
    },
  });

  const { removePublicDocument } = useRemovePublicDocument({
    onSuccess: () => {
      setSelectedFile(null);
      setUploadedFilePath('');
    },
    onError: (error) => {
      console.error('Remove failed:', error);
    },
  });

  const resetModal = useCallback(() => {
    setSelectedFile(null);
    setUploadedFilePath('');
    setDisplayText('');
    setCharacterError('');
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!maxLength) return;

    editor.getEditorState().read(() => {
      const textContent = $getRoot().getTextContent();
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const textToInsert = displayText.trim();
        const selectedLength = selection.getTextContent().length;
        const newLength = textContent.length - selectedLength + textToInsert.length;

        if (newLength > maxLength) {
          const remainingChars = maxLength - (textContent.length - selectedLength);
          setCharacterError(
            remainingChars <= 0
              ? 'Cannot add file - character limit reached'
              : `Display text too long. Only ${remainingChars} characters remaining.`
          );
        } else {
          setCharacterError('');
        }
      }
    });
  }, [editor, displayText, maxLength]);

  const insertFileLink = useCallback(async () => {
    if (!selectedFile) return;

    editor.focus();
    await new Promise((resolve) => requestAnimationFrame(resolve));

    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const fileDisplayText = displayText?.trim() || selectedFile.fileName || 'Uploaded File';

        const textNode = $createTextNode(fileDisplayText);
        selection.insertNodes([textNode]);

        selection.setTextNodeRange(textNode, 0, textNode, fileDisplayText.length);

        editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
          url: selectedFile.publicPath ?? '',
        });

        selection.setTextNodeRange(
          textNode,
          fileDisplayText.length,
          textNode,
          fileDisplayText.length
        );
        selection.insertText(' ');
      }
    });

    resetModal();
  }, [selectedFile, displayText, editor, resetModal]);

  const handleDisplayTextChange = useCallback((value: string) => {
    setDisplayText(value);
    setCharacterError('');
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      return new Promise((resolve, reject) => {
        uploadPublicDocument(
          {
            selectedFile: file,
            AgencyId: agencyId,
            getAuthToken,
          },
          {
            onSuccess: resolve,
            onError: reject,
          }
        );
      });
    },
    [agencyId, getAuthToken, uploadPublicDocument]
  );

  const uploadFileWrapper = useCallback(
    async (
      _authToken: string,
      formData: FormData,
      _campaignId: string,
      _documentType: any,
      onProgress: (progress: any) => void
    ) => {
      const file = formData.get('File') as File;
      if (!file) {
        throw new Error('No file found in FormData');
      }

      onProgress({ loaded: 0, total: 100 });

      const result = await handleFileSelect(file);

      onProgress({ loaded: 100, total: 100 });
      return result;
    },
    [handleFileSelect]
  );

  const handleRemove = useCallback(() => {
    if (!uploadedFilePath) return Promise.resolve(undefined);

    return new Promise<number | undefined>((resolve, reject) => {
      removePublicDocument(
        {
          filePath: uploadedFilePath,
        },
        {
          onSuccess: (result) => {
            resolve(result as unknown as number);
          },
          onError: (error) => {
            reject(error);
          },
        }
      );
    });
  }, [removePublicDocument, uploadedFilePath]);

  const modalContent = (
    <div>
      <AgxColumn gap="spacing-3xl" padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}>
        <AgxColumn gap="spacing-md">
          <AgxFileUpload
            id="fileUpload"
            title="Upload file"
            buttonText={selectedFile ? 'Change' : 'Choose file'}
            uploadFile={uploadFileWrapper}
            campaignId={agencyId || ''}
            getAuthToken={getAuthToken}
            deleteFile={handleRemove}
            extensions={['pdf']}
            required
          />
        </AgxColumn>
        <AgxTextInput
          id="file-display-text"
          label="Display text *"
          type="text"
          hideOptionalText
          onChange={handleDisplayTextChange}
          defaultValue={displayText}
          stretch
          placeholder="Click here"
          disabled={isUploading}
          errorText={characterError}
        />
      </AgxColumn>
    </div>
  );

  const actionButtons = (
    <AgxRow justifyContent="center" gap="spacing-lg" fill>
      <AgxButton
        id="cancel-file"
        variant="hollow"
        size="medium"
        text="Cancel"
        onClick={resetModal}
        disabled={isUploading}
      />
      <AgxButton
        id="upload-file"
        variant="primary"
        size="medium"
        text={'Add File'}
        onClick={insertFileLink}
        disabled={!selectedFile || !displayText?.trim() || isUploading || !!characterError}
      />
    </AgxRow>
  );

  return (
    <>
      <AgxButton
        id="add-file-button"
        variant="naked"
        size="small"
        text={
          <Icon_Upload
            width={16}
            height={16}
            color={`${disabled ? 'var(--fg-disabled)' : 'var(--fg-brand-accent)'}`}
          />
        }
        onClick={() => setIsOpen(true)}
        disabled={disabled}
      />

      {isOpen && (
        <AgxModal
          title="Add File"
          type="single"
          variant="withDivider"
          setIsDialogOpen={setIsOpen}
          content={modalContent}
          actionButtonContainerStyle="withoutDivider"
          actionButtons={actionButtons}
        />
      )}
    </>
  );
};

export default AddFilePlugin;
